import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  ShieldCheck,
  ShieldX,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import {
  MASK_STRATEGIES,
  ROLES,
  RULE_ACTIONS,
  RULE_SCOPES,
  type MaskStrategy,
  type PolicyRule,
  type PolicyRuleInput,
  type Role,
  type RuleAction,
  type RuleScope,
  type SessionUser,
} from '@idas/shared';
import { api, ApiError } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { EmptyState, ListSkeleton } from '../../components/ui/EmptyState';
import { InlineSpinner, RefreshingOverlay } from '../../components/ui/Refreshing';
import { ReasonField } from '../../components/ui/ReasonField';
import { toast } from '../../components/ui/Toast';

const ACTION_STYLES: Record<RuleAction, string> = {
  DENY: 'border-danger/30 bg-danger/10 text-danger',
  MASK: 'border-lock/30 bg-lock/10 text-lock',
  ALLOW: 'border-ok/30 bg-ok/10 text-ok',
};

/** Written for the analyst who hits the block, not for the rule's author. */
const RULE_REASON_PRESETS = [
  'This data is restricted. Ask your manager to request an extract.',
  'Personal data — available only in aggregate. Contact an administrator.',
  'Restricted to HR. Speak to them directly if you need it.',
];

const SCOPE_EXAMPLE: Record<RuleScope, string> = {
  DATABASE: 'hr',
  TABLE: 'hr.salaries',
  COLUMN: '*.*.password_hash',
};

const MASK_BLURB: Record<MaskStrategy, string> = {
  REDACT: 'Replaced entirely. Use when the analyst needs nothing from it.',
  PARTIAL: 'Keeps the last four characters. Good for cards and phone numbers.',
  EMAIL: 'Hides the name, keeps the domain, so grouping by domain still works.',
  HASH: 'Same input always gives the same output — joins and counts work, the value never appears.',
  BUCKET: 'Numbers rounded to a band. Good for salaries and ages.',
};

export function PolicyTab({ me }: { me: SessionUser }) {
  const queryClient = useQueryClient();
  const canWrite = me.permissions.includes('policy:write');

  const [editing, setEditing] = useState<PolicyRule | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PolicyRule | null>(null);
  const [scopeFilter, setScopeFilter] = useState<RuleScope | ''>('');

  const rules = useQuery({ queryKey: ['policy', 'rules'], queryFn: () => api.policy.rules() });
  const mode = useQuery({ queryKey: ['policy', 'mode'], queryFn: api.policy.mode });
  const datasources = useQuery({ queryKey: ['datasources','all'], queryFn: api.datasources.listAll });

  const remove = useMutation({
    mutationFn: (id: string) => api.policy.deleteRule(id),
    onSuccess: () => {
      toast.warning('Rule deleted — access is now wider than it was.');
      void queryClient.invalidateQueries({ queryKey: ['policy'] });
      setConfirmDelete(null);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not delete the rule.'),
  });

  const toggle = useMutation({
    mutationFn: (rule: PolicyRule) =>
      api.policy.updateRule(rule.id, { ...toInput(rule), enabled: !rule.enabled }),
    onSuccess: (_, rule) => {
      if (rule.enabled) toast.warning('Rule disabled — it no longer restricts anything.');
      else toast.success('Rule enabled.');
      void queryClient.invalidateQueries({ queryKey: ['policy'] });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not update the rule.'),
  });

  const visible = useMemo(
    () => (rules.data?.rules ?? []).filter((rule) => !scopeFilter || rule.scope === scopeFilter),
    [rules.data, scopeFilter],
  );

  return (
    <div className="space-y-4">
      {/* Mode banner. The single highest-impact setting in the system, so it
          sits above the rules rather than buried in a settings page.

          It gets a placeholder of its own height while loading: without one
          the tab opened on blank space and then shifted everything down as the
          banner arrived, which reads as a glitch rather than as loading. */}
      {mode.isLoading && (
        <div className="h-[4.75rem] animate-pulse rounded-lg border border-edge bg-surface-sunken" />
      )}

      {mode.data && (
        <div
          className={clsx(
            'flex flex-wrap items-center gap-3 rounded-lg border p-3',
            mode.data.mode === 'DENYLIST'
              ? 'border-warn/30 bg-warn/5'
              : 'border-ok/30 bg-ok/5',
          )}
        >
          {mode.data.mode === 'DENYLIST' ? (
            <AlertTriangle className="h-4 w-4 shrink-0 text-warn" />
          ) : (
            <ShieldCheck className="h-4 w-4 shrink-0 text-ok" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-2xs font-semibold text-ink">
              {mode.data.mode === 'DENYLIST'
                ? 'Denylist mode — everything is readable unless a rule forbids it'
                : 'Allowlist mode — nothing is readable unless a rule permits it'}
            </p>
            <p className="mt-0.5 text-2xs leading-relaxed text-ink-muted">
              {mode.data.mode === 'DENYLIST'
                ? 'A table added to the database tomorrow is readable by every analyst the moment it appears. The Review tab lists objects no rule covers — that queue is what compensates for this.'
                : 'New objects are unreachable until someone grants them. Tighter, but every new table needs an explicit rule before anyone can use it.'}
            </p>
          </div>
        </div>
      )}

      <section className="panel rounded-lg">
        <header className="flex flex-wrap items-center gap-2 border-b border-edge px-3 py-2">
          <ShieldX className="h-3.5 w-3.5 text-ink-muted" />
          <h2 className="text-xs font-semibold text-ink">Rules</h2>
          <span className="text-2xs text-ink-faint">{visible.length}</span>
          {rules.isFetching && !rules.isLoading && <InlineSpinner />}

          <div className="flex-1" />

          <select
            className="input w-auto py-1"
            value={scopeFilter}
            onChange={(event) => setScopeFilter(event.target.value as RuleScope | '')}
          >
            <option value="">All scopes</option>
            {RULE_SCOPES.map((scope) => (
              <option key={scope} value={scope}>
                {scope.toLowerCase()}
              </option>
            ))}
          </select>

          {canWrite && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              <Plus className="h-3 w-3" />
              New rule
            </button>
          )}
        </header>

        {rules.isLoading && <ListSkeleton rows={6} height={48} />}

        {!rules.isLoading && visible.length === 0 && (
          <EmptyState
            icon={ShieldX}
            title="No rules"
            body="In denylist mode that means every table and column on every connection is readable by every analyst. Start with credential columns and anything holding personal data."
          />
        )}

        <RefreshingOverlay active={rules.isFetching && !rules.isLoading}>
        <div className="divide-y divide-edge">
          {visible.map((rule) => (
            <div
              key={rule.id}
              className={clsx('flex flex-wrap items-center gap-3 px-3 py-2', !rule.enabled && 'opacity-50')}
            >
              <span className={clsx('chip w-16 justify-center', ACTION_STYLES[rule.action])}>
                {rule.action.toLowerCase()}
              </span>

              <span className="chip border-edge bg-surface-sunken text-ink-muted">
                {rule.scope.toLowerCase()}
              </span>

              <code className="min-w-0 flex-1 truncate font-mono text-2xs text-ink">
                {rule.pattern}
              </code>

              {rule.maskStrategy && (
                <span className="chip border-lock/30 bg-lock/10 text-lock">
                  {rule.maskStrategy.toLowerCase()}
                </span>
              )}

              {rule.appliesToRoles && (
                <span className="text-2xs text-ink-faint" title="Applies only to these roles">
                  {rule.appliesToRoles.map((r) => r.toLowerCase()).join(', ')}
                </span>
              )}

              {rule.dataSourceId === null && (
                <span className="chip border-accent/30 bg-accent/10 text-accent" title="Applies to every connection">
                  global
                </span>
              )}

              <p className="w-full truncate text-2xs italic text-ink-muted md:w-auto md:max-w-xs">
                {rule.reason}
              </p>

              {canWrite && (
                <div className="flex shrink-0 gap-0.5">
                  <button
                    className="btn btn-ghost px-1.5"
                    onClick={() => toggle.mutate(rule)}
                    title={rule.enabled ? 'Disable this rule' : 'Enable this rule'}
                  >
                    {rule.enabled ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    className="btn btn-ghost px-1.5"
                    onClick={() => setEditing(rule)}
                    aria-label="Edit rule"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="btn btn-ghost px-1.5 hover:text-danger"
                    onClick={() => setConfirmDelete(rule)}
                    aria-label="Delete rule"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        </RefreshingOverlay>
      </section>

      {editing && (
        <RuleModal
          rule={editing === 'new' ? null : editing}
          datasourceOptions={datasources.data?.datasources ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['policy'] });
            setEditing(null);
          }}
        />
      )}

      {confirmDelete && (
        <Modal
          title="Delete this rule?"
          width="sm"
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmDelete(null)}>
                Keep it
              </button>
              <button
                className="btn border-transparent bg-danger text-white hover:bg-danger/90"
                onClick={() => remove.mutate(confirmDelete.id)}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Delete rule'}
              </button>
            </>
          }
        >
          <code className="mb-3 block break-all rounded border border-edge bg-surface-sunken p-2 font-mono text-2xs text-ink">
            {confirmDelete.action} {confirmDelete.scope} {confirmDelete.pattern}
          </code>
          <p className="text-2xs leading-relaxed text-ink-muted">
            Deleting a rule always widens access. If you only want to pause it, disable it instead
            — that keeps the pattern and reason on record.
          </p>
        </Modal>
      )}
    </div>
  );
}

function toInput(rule: PolicyRule): PolicyRuleInput {
  return {
    dataSourceId: rule.dataSourceId,
    scope: rule.scope,
    pattern: rule.pattern,
    action: rule.action,
    maskStrategy: rule.maskStrategy,
    appliesToRoles: rule.appliesToRoles,
    reason: rule.reason,
    enabled: rule.enabled,
  };
}

function RuleModal({
  rule,
  datasourceOptions,
  onClose,
  onSaved,
}: {
  rule: PolicyRule | null;
  datasourceOptions: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<PolicyRuleInput>(
    rule
      ? toInput(rule)
      : {
          dataSourceId: null,
          scope: 'COLUMN',
          pattern: '',
          action: 'DENY',
          maskStrategy: null,
          appliesToRoles: null,
          reason: '',
          enabled: true,
        },
  );

  const set = <K extends keyof PolicyRuleInput>(key: K, value: PolicyRuleInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  /**
   * Live blast-radius preview.
   *
   * Writing `hr.*` when you meant `hr.**`, or `*.*.email` when you meant
   * `crm.*.email`, is easy and expensive — the rule looks right and silently
   * covers the wrong set. Showing exactly which objects match, before saving,
   * turns that from an incident into a typo.
   */
  const previewSourceId = form.dataSourceId ?? datasourceOptions[0]?.id ?? '';
  const [debouncedPattern, setDebouncedPattern] = useState(form.pattern);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPattern(form.pattern), 350);
    return () => clearTimeout(timer);
  }, [form.pattern]);

  const preview = useQuery({
    queryKey: ['policy', 'preview', debouncedPattern, form.scope, previewSourceId],
    queryFn: () =>
      api.policy.testPattern({
        pattern: debouncedPattern,
        scope: form.scope,
        dataSourceId: previewSourceId,
      }),
    enabled: Boolean(debouncedPattern.trim() && previewSourceId),
  });

  const save = useMutation({
    mutationFn: () =>
      rule ? api.policy.updateRule(rule.id, form) : api.policy.createRule(form),
    onSuccess: () => {
      toast.success(rule ? 'Rule updated.' : 'Rule created.');
      onSaved();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save the rule.'),
  });

  const segments = form.pattern.split('.').length;
  const expectedSegments = { DATABASE: 1, TABLE: 2, COLUMN: 3 }[form.scope];
  const segmentMismatch = form.pattern.trim().length > 0 && segments !== expectedSegments;
  const reasonTooShort = form.reason.trim().length < 10;

  const patternRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const [attempted, setAttempted] = useState(false);

  const blocker = !form.pattern.trim()
    ? { field: 'pattern' as const, message: 'Enter a pattern for this rule to match.' }
    : segmentMismatch
      ? {
          field: 'pattern' as const,
          message: `A ${form.scope.toLowerCase()} pattern needs ${expectedSegments} dot-separated part(s).`,
        }
      : reasonTooShort
        ? {
            field: 'reason' as const,
            message: `Add a reason of at least 10 characters (${form.reason.trim().length} so far) — the analyst sees it.`,
          }
        : null;

  // Clickable while incomplete, so pressing it points at what is missing
  // rather than doing nothing. See the same pattern in UsersTab.
  const attemptSave = () => {
    if (!blocker) {
      save.mutate();
      return;
    }
    setAttempted(true);
    const target = blocker.field === 'pattern' ? patternRef.current : reasonRef.current;
    target?.focus();
    target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  return (
    <Modal
      title={rule ? 'Edit rule' : 'New policy rule'}
      description="Patterns use * within one name segment and ** across segments."
      width="lg"
      onClose={onClose}
      footer={
        <>
          {attempted && blocker && (
            <span className="mr-auto max-w-[22rem] text-2xs leading-snug text-warn">
              {blocker.message}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={attemptSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : rule ? 'Save changes' : 'Create rule'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="mb-1 block text-2xs font-medium text-ink-muted">Scope</span>
            <select
              className="input"
              value={form.scope}
              onChange={(event) => set('scope', event.target.value as RuleScope)}
            >
              {RULE_SCOPES.map((scope) => (
                <option key={scope} value={scope}>
                  {scope.toLowerCase()}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-2xs font-medium text-ink-muted">Action</span>
            <select
              className="input"
              value={form.action}
              onChange={(event) => {
                const action = event.target.value as RuleAction;
                set('action', action);
                // Masking only means anything on a column, and only a MASK
                // rule carries a strategy. Keep the form self-consistent so
                // the server never has to reject an incoherent combination.
                if (action !== 'MASK') set('maskStrategy', null);
                else set('scope', 'COLUMN');
              }}
            >
              {RULE_ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {action.toLowerCase()}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-2xs font-medium text-ink-muted">Connection</span>
            <select
              className="input"
              value={form.dataSourceId ?? ''}
              onChange={(event) => set('dataSourceId', event.target.value || null)}
            >
              <option value="">All connections</option>
              {datasourceOptions.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-2xs font-medium text-ink-muted">Pattern</span>
          <input
            ref={patternRef}
            className={clsx(
              'input font-mono',
              (segmentMismatch || (attempted && !form.pattern.trim())) && 'border-danger',
            )}
            value={form.pattern}
            onChange={(event) => set('pattern', event.target.value)}
            placeholder={SCOPE_EXAMPLE[form.scope]}
            autoFocus
          />
          {segmentMismatch ? (
            <span className="mt-1 block text-2xs text-danger">
              A {form.scope.toLowerCase()} pattern needs {expectedSegments} dot-separated part
              {expectedSegments > 1 ? 's' : ''} — for example{' '}
              <code className="font-mono">{SCOPE_EXAMPLE[form.scope]}</code>.
            </span>
          ) : (
            <span className="mt-1 block text-2xs text-ink-faint">
              e.g. <code className="font-mono">{SCOPE_EXAMPLE[form.scope]}</code>
            </span>
          )}
        </label>

        {form.action === 'MASK' && (
          <div>
            <span className="mb-1 block text-2xs font-medium text-ink-muted">
              How should the value be rewritten?
            </span>
            <div className="space-y-1">
              {MASK_STRATEGIES.map((strategy) => (
                <button
                  key={strategy}
                  className={clsx(
                    'flex w-full items-start gap-2 rounded-md border p-2 text-left transition-colors',
                    form.maskStrategy === strategy
                      ? 'border-lock bg-lock/5'
                      : 'border-edge hover:bg-surface-sunken',
                  )}
                  onClick={() => set('maskStrategy', strategy)}
                >
                  <span
                    className={clsx(
                      'mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full border',
                      form.maskStrategy === strategy ? 'border-lock bg-lock' : 'border-edge-strong',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block text-2xs font-medium text-ink">
                      {strategy.toLowerCase()}
                    </span>
                    <span className="block text-2xs leading-relaxed text-ink-muted">
                      {MASK_BLURB[strategy]}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Blast radius ─────────────────────────────────────────────── */}
        <div className="rounded-md border border-edge bg-surface-sunken">
          <div className="flex items-center gap-2 border-b border-edge px-2.5 py-1.5">
            <span className="text-2xs font-medium text-ink-muted">This pattern matches</span>
            {preview.data && (
              <span
                className={clsx(
                  'chip',
                  preview.data.total === 0
                    ? 'border-warn/30 bg-warn/10 text-warn'
                    : 'border-accent/30 bg-accent/10 text-accent',
                )}
              >
                {preview.data.total} object{preview.data.total === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <div className="max-h-40 overflow-y-auto p-2">
            {!debouncedPattern.trim() && (
              <p className="text-2xs text-ink-faint">Type a pattern to see what it covers.</p>
            )}
            {preview.isFetching && (
              <p className="text-2xs text-ink-faint">Checking against the catalog…</p>
            )}
            {preview.data?.total === 0 && (
              <p className="text-2xs leading-relaxed text-warn">
                Nothing in the catalog matches. The rule will save and do nothing — check the
                spelling, or refresh the schema if the object is new.
              </p>
            )}
            {preview.data && preview.data.matched.length > 0 && (
              <div className="space-y-0.5">
                {preview.data.matched.map((name) => (
                  <code key={name} className="block truncate font-mono text-2xs text-ink">
                    {name}
                  </code>
                ))}
                {preview.data.truncated && (
                  <p className="pt-1 text-2xs text-ink-faint">
                    …and {preview.data.total - preview.data.matched.length} more.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <span className="mb-1 block text-2xs font-medium text-ink-muted">
            Applies to (leave empty for everyone)
          </span>
          <div className="flex flex-wrap gap-1.5">
            {ROLES.map((role) => {
              const selected = form.appliesToRoles?.includes(role) ?? false;
              return (
                <button
                  key={role}
                  className={clsx('btn', selected && 'btn-primary')}
                  onClick={() => {
                    const current = form.appliesToRoles ?? [];
                    const next: Role[] = selected
                      ? current.filter((r) => r !== role)
                      : [...current, role];
                    set('appliesToRoles', next.length ? next : null);
                  }}
                >
                  {role.replace('_', ' ').toLowerCase()}
                </button>
              );
            })}
          </div>
        </div>

        <ReasonField
          ref={reasonRef}
          id="rule-reason"
          label="Reason shown to the analyst"
          value={form.reason}
          onChange={(value) => set('reason', value)}
          minLength={10}
          presets={RULE_REASON_PRESETS}
          placeholder="e.g. Salary data is restricted to HR. Ask your manager to request an extract."
          showError={attempted}
          hint="Displayed verbatim when a query is blocked. A vague reason just becomes a support ticket someone has to answer anyway."
        />
      </div>
    </Modal>
  );
}
